import { createFileRoute, Link, useParams, useNavigate } from "@tanstack/react-router";
import { DashboardShell } from "@/components/DashboardShell";
import { PARTNER_NAV } from "@/lib/nav";
import { ServiceActivationWizard } from "@/components/ServiceActivationWizard";
import {
  ACTIVATION_PIPELINE,
  SERVICES,
  SERVICE_AGREEMENTS,
  SERVICE_LABEL,
  SERVICE_LISTING_TYPES,
  SERVICE_STATUS_META,
  SERVICE_VERIFICATION_ITEMS,
  isServiceLive,
  isServiceType,
  useMyServices,
} from "@/lib/services";

export const Route = createFileRoute("/_authenticated/partner/services/$serviceType")({
  head: () => ({
    meta: [
      { title: "Service profile · Landoo Partner" },
      {
        name: "description",
        content:
          "Registration, verification, agreement and subscription status for one Landoo partner service.",
      },
    ],
  }),
  component: ServiceDetailPage,
});

function ServiceDetailPage() {
  const { serviceType } = useParams({ from: "/_authenticated/partner/services/$serviceType" });
  const nav = useNavigate();
  const { profiles, refetch, isLoading } = useMyServices();

  if (!isServiceType(serviceType)) {
    return (
      <DashboardShell title="Service" role="Partner" items={PARTNER_NAV}>
        <div className="rounded-2xl border border-border bg-card p-5 text-sm text-muted-foreground">
          Unknown service.
        </div>
      </DashboardShell>
    );
  }

  const service = SERVICES.find((s) => s.value === serviceType)!;
  const profile = profiles.find((p) => p.service_type === serviceType);
  const live = profile ? isServiceLive(profile) : false;

  return (
    <DashboardShell
      title={`${SERVICE_LABEL[serviceType]} service`}
      role="Partner"
      items={PARTNER_NAV}
    >
      <div className="rounded-2xl border border-border bg-card p-5 shadow-soft">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <div className="text-base font-bold text-brown-dark">{service.cardTitle}</div>
            <div className="text-xs text-muted-foreground">{service.blurb}</div>
          </div>
          <span
            className={`rounded-full px-3 py-1 text-[11px] font-semibold ${
              SERVICE_STATUS_META[profile?.service_status ?? "not_activated"].cls
            }`}
          >
            {SERVICE_STATUS_META[profile?.service_status ?? "not_activated"].label}
          </span>
        </div>
        {profile && (
          <div className="mt-4 grid gap-2 sm:grid-cols-2">
            <Info k="Partner role" v={profile.partner_role ?? "—"} />
            <Info
              k="Registration"
              v={profile.service_status === "registration_draft" ? "Draft" : "Submitted"}
            />
            <Info k="Verification" v={profile.verification_status} />
            <Info
              k="Agreement"
              v={`${profile.agreement_id ?? "—"} · ${profile.agreement_status}`}
            />
            <Info k="Subscription" v={profile.subscription_status} />
            <Info k="Approval" v={profile.approval_status} />
            <Info
              k="Activated"
              v={profile.activated_at ? new Date(profile.activated_at).toLocaleDateString() : "—"}
            />
            <Info
              k="Expires"
              v={profile.expires_at ? new Date(profile.expires_at).toLocaleDateString() : "—"}
            />
          </div>
        )}
        {profile?.admin_notes && (
          <div className="mt-3 rounded-xl border border-dashed border-border bg-cream/50 p-3 text-xs text-brown-dark">
            Admin note: {profile.admin_notes}
          </div>
        )}
      </div>

      {profile && (
        <div className="mt-4 rounded-2xl border border-border bg-card p-4 shadow-soft">
          <div className="mb-3 text-sm font-bold text-brown-dark">Activation status</div>
          <ol className="flex flex-wrap gap-2">
            {ACTIVATION_PIPELINE.map((s, i) => {
              const order = ACTIVATION_PIPELINE.findIndex(
                (x) => x.status === profile.service_status,
              );
              const done = order >= i && order !== -1;
              return (
                <li
                  key={s.status}
                  className={`rounded-full px-3 py-1 text-[11px] font-semibold ${
                    done ? "bg-brand-soft text-brown-dark" : "bg-secondary text-muted-foreground"
                  }`}
                >
                  {i + 1}. {s.label}
                </li>
              );
            })}
          </ol>
          <p className="mt-2 text-[11px] text-muted-foreground">
            This pipeline belongs to the {SERVICE_LABEL[serviceType]} service only and never affects
            other services.
          </p>
        </div>
      )}

      <div className="mt-4 grid gap-3 md:grid-cols-2">
        <Card title={`${SERVICE_LABEL[serviceType]} listing types`}>
          <div className="flex flex-wrap gap-1.5">
            {SERVICE_LISTING_TYPES[serviceType].map((t) => (
              <span
                key={t}
                className="rounded-full bg-secondary px-2.5 py-1 text-[11px] font-semibold text-brown-dark"
              >
                {t}
              </span>
            ))}
          </div>
        </Card>
        <Card title={`${SERVICE_LABEL[serviceType]} agreements`}>
          <ul className="space-y-1 text-xs text-muted-foreground">
            {SERVICE_AGREEMENTS[serviceType].map((a) => (
              <li
                key={a}
                className={profile?.agreement_id === a ? "font-semibold text-brown-dark" : ""}
              >
                {a}
                {profile?.agreement_id === a ? " · signed" : ""}
              </li>
            ))}
          </ul>
        </Card>
        <Card title={`${SERVICE_LABEL[serviceType]} verification requirements`}>
          <ul className="space-y-1 text-xs text-muted-foreground">
            {SERVICE_VERIFICATION_ITEMS[serviceType].map((v) => (
              <li key={v}>{v}</li>
            ))}
          </ul>
        </Card>
        <Card title="Listing permission">
          <p className="text-xs text-muted-foreground">
            {live
              ? `You can create ${SERVICE_LABEL[serviceType]} listings.`
              : `${SERVICE_LABEL[serviceType]} listings are blocked until this service is approved and active.`}
          </p>
          {live && (
            <Link
              to={serviceType === "parking" ? "/partner/parking/new" : "/partner/properties/new"}
              className="mt-2 inline-flex rounded-full bg-coral px-3.5 py-2 text-xs font-semibold text-white shadow-soft"
            >
              Create {SERVICE_LABEL[serviceType]} listing
            </Link>
          )}
        </Card>
      </div>

      {!isLoading &&
        (!profile ||
          ["registration_draft", "rejected", "expired", "not_activated"].includes(
            profile.service_status,
          )) && (
          <div className="mt-5">
            <h2 className="mb-2 text-base font-bold text-brown-dark">
              {profile ? "Continue activation" : `Activate ${SERVICE_LABEL[serviceType]}`}
            </h2>
            <ServiceActivationWizard
              serviceType={serviceType}
              profile={profile}
              onDone={() => {
                void refetch();
                nav({ to: "/partner/services" });
              }}
            />
          </div>
        )}
    </DashboardShell>
  );
}

function Info({ k, v }: { k: string; v: string }) {
  return (
    <div className="rounded-xl border border-border bg-background px-3 py-2">
      <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        {k}
      </div>
      <div className="text-sm font-semibold capitalize text-brown-dark">{v.replace(/_/g, " ")}</div>
    </div>
  );
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-4 shadow-soft">
      <div className="mb-2 text-sm font-bold text-brown-dark">{title}</div>
      {children}
    </div>
  );
}
